import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  EnvelopeIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  ShoppingBagIcon,
  CheckIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';

const PERKS = [
  'Akses ke seluruh katalog produk',
  'Simpan wishlist dan riwayat pesanan',
  'Promo eksklusif untuk member',
];

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!email.trim() || !password.trim()) {
      toast.error('Email dan password wajib diisi.');
      return;
    }

    toast(`Halo ${email}, halaman login masih dalam perbaikan 🙏`);
  };

  const handleSocial = (provider) => {
    toast(`${provider} sign-in segera hadir`);
  };

  return (
    <div className="relative min-h-[calc(100vh-4rem)] overflow-hidden bg-soft-gradient lg:min-h-[calc(100vh-5rem)]">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 dot-grid-bg opacity-50 [mask-image:radial-gradient(60%_50%_at_50%_40%,black,transparent)]" />
        <div className="absolute -left-24 top-10 h-72 w-72 animate-blob rounded-full bg-brand-300/30 blur-3xl" />
        <div className="absolute -right-20 bottom-0 h-80 w-80 animate-blob rounded-full bg-fuchsia-300/25 blur-3xl [animation-delay:-7s]" />
      </div>

      <div className="container-page relative flex min-h-[inherit] items-center justify-center py-10 sm:py-14">
        <div className="grid w-full max-w-4xl overflow-hidden rounded-[2rem] border border-white/70 bg-white shadow-lift lg:grid-cols-2">
          {/* Brand panel */}
          <aside className="relative hidden overflow-hidden bg-brand-gradient bg-[length:200%_200%] p-10 text-white animate-gradient-pan lg:flex lg:flex-col">
            <div className="pointer-events-none absolute inset-0 dot-grid-bg opacity-20" aria-hidden="true" />
            <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-2xl" />

            <NavLink to="/" className="relative flex items-center gap-2.5">
              <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/15 backdrop-blur">
                <ShoppingBagIcon className="h-5 w-5" />
              </span>
              <span className="text-base font-extrabold">Shopping Store</span>
            </NavLink>

            <div className="relative mt-auto">
              <h2 className="text-3xl font-extrabold leading-tight">
                Belanja lebih pintar,
                <br />
                mulai dari sini.
              </h2>
              <p className="mt-3 max-w-xs text-sm leading-6 text-white/80">
                Satu akun untuk semua kebutuhan belanjamu. Simpan favorit,
                lacak pesanan, dan nikmati promo member.
              </p>

              <ul className="mt-7 space-y-3">
                {PERKS.map((perk) => (
                  <li key={perk} className="flex items-center gap-3 text-sm text-white/90">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/15">
                      <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} />
                    </span>
                    {perk}
                  </li>
                ))}
              </ul>
            </div>

            <p className="relative mt-10 text-xs text-white/60">
              © {new Date().getFullYear()} Shopping Store
            </p>
          </aside>

          {/* Form panel */}
          <div className="p-6 sm:p-10">
            <NavLink
              to="/"
              className="group inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 transition hover:text-brand-700 lg:hidden"
            >
              <ArrowLeftIcon className="h-3.5 w-3.5 transition group-hover:-translate-x-0.5" />
              Kembali ke home
            </NavLink>

            <div className="mt-6 lg:mt-0">
              <h1 className="text-2xl font-extrabold text-slate-900 sm:text-3xl">
                Selamat datang kembali
              </h1>
              <p className="mt-2 text-sm text-slate-500">
                Masuk ke akunmu untuk melanjutkan belanja.
              </p>
            </div>

            <form className="mt-8 space-y-5" onSubmit={handleSubmit} noValidate>
              <div>
                <label htmlFor="email" className="mb-1.5 block text-sm font-semibold text-slate-700">
                  Email
                </label>
                <div className="relative">
                  <EnvelopeIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    placeholder="nama@email.com"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    className="input-base !py-3 pl-11"
                  />
                </div>
              </div>

              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label htmlFor="password" className="block text-sm font-semibold text-slate-700">
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={() => toast('Reset password segera hadir')}
                    className="text-xs font-semibold text-brand-600 transition hover:text-brand-800"
                  >
                    Lupa password?
                  </button>
                </div>
                <div className="relative">
                  <LockClosedIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    placeholder="••••••••"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    className="input-base !py-3 pl-11 pr-11"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    aria-label={showPassword ? 'Sembunyikan password' : 'Tampilkan password'}
                    className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-4 w-4" />
                    ) : (
                      <EyeIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <label className="flex cursor-pointer items-center gap-2.5 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(event) => setRemember(event.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                />
                Ingat saya
              </label>

              <button type="submit" className="btn-primary w-full">
                Sign in
              </button>
            </form>

            <div className="my-6 flex items-center gap-3">
              <span className="h-px flex-1 bg-slate-200" />
              <span className="text-xs font-medium text-slate-400">atau lanjutkan dengan</span>
              <span className="h-px flex-1 bg-slate-200" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button type="button" onClick={() => handleSocial('Google')} className="btn-secondary !py-2.5">
                <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
                  <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5a5.6 5.6 0 0 1-2.4 3.7v3h3.9c2.3-2.1 3.5-5.2 3.5-8.9Z" />
                  <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.9-3c-1.1.7-2.4 1.2-4 1.2-3.1 0-5.7-2.1-6.6-4.9H1.4v3.1A12 12 0 0 0 12 24Z" />
                  <path fill="#FBBC05" d="M5.4 14.4a7.2 7.2 0 0 1 0-4.6V6.7H1.4a12 12 0 0 0 0 10.8l4-3.1Z" />
                  <path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.5 1.8l3.4-3.4A12 12 0 0 0 1.4 6.7l4 3.1C6.3 6.9 8.9 4.8 12 4.8Z" />
                </svg>
                Google
              </button>
              <button type="button" onClick={() => handleSocial('GitHub')} className="btn-secondary !py-2.5">
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
                  <path d="M12 2C6.48 2 2 6.58 2 12.26c0 4.5 2.87 8.32 6.84 9.67.5.1.68-.22.68-.48v-1.7c-2.78.62-3.37-1.37-3.37-1.37-.45-1.18-1.11-1.5-1.11-1.5-.9-.64.07-.62.07-.62 1 .07 1.53 1.06 1.53 1.06.9 1.57 2.34 1.12 2.91.86.09-.67.35-1.12.63-1.38-2.22-.26-4.56-1.14-4.56-5.06 0-1.12.39-2.03 1.03-2.75-.1-.26-.45-1.3.1-2.7 0 0 .84-.28 2.75 1.05a9.36 9.36 0 0 1 5 0c1.91-1.33 2.75-1.05 2.75-1.05.55 1.4.2 2.44.1 2.7.64.72 1.03 1.63 1.03 2.75 0 3.93-2.35 4.8-4.58 5.05.36.32.68.94.68 1.9v2.82c0 .27.18.59.69.48A10.14 10.14 0 0 0 22 12.26C22 6.58 17.52 2 12 2Z" />
                </svg>
                GitHub
              </button>
            </div>

            <p className="mt-7 text-center text-sm text-slate-500">
              Belum punya akun?{' '}
              <button
                type="button"
                onClick={() => toast('Pendaftaran segera hadir')}
                className="font-semibold text-brand-600 transition hover:text-brand-800"
              >
                Daftar gratis
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
